import {
  FM1_GLOBAL_PARAMETER_START,
  FM1_OPERATOR_COUNT,
  FM1_OPERATOR_PARAMETER_COUNT,
  FM1_VOICE_NAME_LENGTH,
  FM1_VOICE_NAME_START,
  FM1_VOICE_PARAMETER_COUNT,
  getGlobalParameterDefinition,
} from '@/lib/fm1-parameters'

export const dx7BankVoiceCount = 32
export const dx7PackedVoiceSize = 128
const dx7BankDataSize = dx7BankVoiceCount * dx7PackedVoiceSize
export const dx7BankFileSize = dx7BankDataSize + 8
/** The most banks one file may join, so a large file chosen by mistake is refused unread. */
export const dx7BankArchiveMaximumBanks = 256
const feedbackIndex = getGlobalParameterDefinition('global.feedback').voiceIndex
const oscillatorSyncIndex = getGlobalParameterDefinition('global.oscillatorSync').voiceIndex
const lfoSpeedIndex = getGlobalParameterDefinition('global.lfoSpeed').voiceIndex
const lfoKeySyncIndex = getGlobalParameterDefinition('global.lfoKeySync').voiceIndex
const lfoWaveIndex = getGlobalParameterDefinition('global.lfoWave').voiceIndex
const pitchModSensitivityIndex = getGlobalParameterDefinition(
  'global.pitchModSensitivity',
).voiceIndex
const transposeIndex = getGlobalParameterDefinition('global.transpose').voiceIndex

export type Dx7Voice = { data: Uint8Array; name: string }

/** Yamaha's bulk-dump checksum: the two's complement of the data sum, in seven bits. */
export function dx7Checksum(bytes: Uint8Array) {
  return (128 - (bytes.reduce((sum, byte) => sum + byte, 0) & 0x7f)) & 0x7f
}

export function isSevenBitData(bytes: Uint8Array) {
  return bytes.every((byte) => byte <= 0x7f)
}

function assertPackedVoiceLength(voice: Dx7Voice) {
  if (voice.data.length !== dx7PackedVoiceSize) {
    throw new Error(
      `Expected a ${dx7PackedVoiceSize}-byte packed DX7 voice; received ${voice.data.length} bytes.`,
    )
  }
}

function assertPackedVoice(voice: Dx7Voice) {
  assertPackedVoiceLength(voice)
  if (!isSevenBitData(voice.data)) {
    throw new Error('Packed DX7 voice data must contain only 7-bit values.')
  }
}

/**
 * Reads a voice as an earlier release saved it in browser storage. Bytes above seven bits are
 * masked to the range a DX7 voice can carry; anything that is not a packed voice returns null.
 */
export function normalizeStoredDx7Voice(value: unknown): Dx7Voice | null {
  if (!value || typeof value !== 'object') return null
  const { data, name } = value as Partial<Dx7Voice>
  if (!(data instanceof Uint8Array) || data.length !== dx7PackedVoiceSize) return null

  const normalized = isSevenBitData(data) ? data : data.map((byte) => byte & 0x7f)
  return { data: normalized, name: typeof name === 'string' ? name : decodeVoiceName(normalized) }
}

// 'too-large' is a file larger than the most banks a file may join (src/lib/dx7-bank-archive.ts).
type Dx7BankFileProblem = 'checksum' | 'format' | 'high-bit-data' | 'size' | 'too-large'

/** A bank file that cannot be imported, with a problem code the UI can explain in any language. */
export class Dx7BankFileError extends Error {
  readonly problem: Dx7BankFileProblem
  readonly receivedBytes: number

  constructor(problem: Dx7BankFileProblem, message: string, receivedBytes: number) {
    super(message)
    this.name = 'Dx7BankFileError'
    this.problem = problem
    this.receivedBytes = receivedBytes
  }
}

/**
 * A catalog bank that could not be downloaded, for example while offline. It lives here rather than
 * beside the catalog loader so the eager bank error messages can recognise it without bringing the
 * catalog list into the initial bundle.
 */
export class Dx7CatalogBankUnavailableError extends Error {
  constructor(bankName: string, cause?: unknown) {
    super(`The ${bankName} sound bank could not be loaded.`, { cause })
    this.name = 'Dx7CatalogBankUnavailableError'
  }
}

function bankSizeError(receivedBytes: number) {
  return new Dx7BankFileError(
    'size',
    `Expected a ${dx7BankFileSize}-byte DX7 bank; received ${receivedBytes} bytes.`,
    receivedBytes,
  )
}

export function parseDx7Bank(file: ArrayBuffer): Dx7Voice[] {
  const bytes = new Uint8Array(file)
  if (bytes.length !== dx7BankFileSize) throw bankSizeError(bytes.length)
  if (
    bytes[0] !== 0xf0 ||
    bytes[1] !== 0x43 ||
    bytes[3] !== 0x09 ||
    bytes[4] !== 0x20 ||
    bytes[5] !== 0x00 ||
    bytes.at(-1) !== 0xf7
  ) {
    throw new Dx7BankFileError(
      'format',
      'This is not a Yamaha DX7 32-voice bulk SysEx bank.',
      bytes.length,
    )
  }
  const voiceData = bytes.slice(6, 6 + dx7BankDataSize)
  if (!isSevenBitData(voiceData)) {
    throw new Dx7BankFileError(
      'high-bit-data',
      'The DX7 bank contains data bytes outside the 7-bit MIDI range.',
      bytes.length,
    )
  }
  const checksum = dx7Checksum(voiceData)
  if (checksum !== bytes.at(-2)) {
    throw new Dx7BankFileError('checksum', 'The DX7 bank checksum is invalid.', bytes.length)
  }

  return Array.from({ length: dx7BankVoiceCount }, (_, index) => {
    const data = voiceData.slice(index * dx7PackedVoiceSize, (index + 1) * dx7PackedVoiceSize)
    return { data, name: decodeVoiceName(data) }
  })
}

export function updateDx7VoiceName(voice: Dx7Voice, name: string): Dx7Voice {
  const data = voice.data.slice()
  const normalized = name.slice(0, FM1_VOICE_NAME_LENGTH).padEnd(FM1_VOICE_NAME_LENGTH, ' ')
  for (let index = 0; index < FM1_VOICE_NAME_LENGTH; index += 1) {
    const code = normalized.charCodeAt(index)
    data[118 + index] = code >= 0x20 && code <= 0x7e ? code : 0x20
  }
  return { data, name: decodeVoiceName(data) }
}

export function makeDx7VoiceNameEdits(
  parameters: Uint8Array,
  name: string,
): [parameter: number, value: number][] {
  const normalized = name.slice(0, FM1_VOICE_NAME_LENGTH).padEnd(FM1_VOICE_NAME_LENGTH, ' ')

  return Array.from({ length: FM1_VOICE_NAME_LENGTH }, (_, offset) => {
    const code = normalized.charCodeAt(offset)
    const value = code >= 0x20 && code <= 0x7e ? code : 0x20
    return [FM1_VOICE_NAME_START + offset, value] as [number, number]
  }).filter(([parameter, value]) => parameters[parameter] !== value)
}

/** Converts a packed 128-byte bank voice to the DX7's 155-byte edit-buffer format. */
export function unpackDx7Voice(voice: Dx7Voice) {
  // A short voice would otherwise read its missing bytes as zeros and unpack too few parameters.
  assertPackedVoiceLength(voice)
  const packed = voice.data
  const unpacked: number[] = []

  for (let operator = 0; operator < FM1_OPERATOR_COUNT; operator += 1) {
    const offset = operator * 17
    unpacked.push(
      ...packed.slice(offset, offset + 11),
      packed[offset + 11] & 0x03,
      (packed[offset + 11] >> 2) & 0x03,
      packed[offset + 12] & 0x07,
      packed[offset + 13] & 0x03,
      (packed[offset + 13] >> 2) & 0x07,
      packed[offset + 14],
      packed[offset + 15] & 0x01,
      (packed[offset + 15] >> 1) & 0x1f,
      packed[offset + 16],
      (packed[offset + 12] >> 3) & 0x0f,
    )
  }

  unpacked.push(
    ...packed.slice(102, 110),
    packed[110] & 0x1f,
    packed[111] & 0x07,
    (packed[111] >> 3) & 0x01,
    ...packed.slice(112, 116),
    packed[116] & 0x01,
    (packed[116] >> 1) & 0x07,
    (packed[116] >> 4) & 0x07,
    ...packed.slice(117, 128),
  )

  return Uint8Array.from(unpacked)
}

/** Converts the DX7's 155-byte edit-buffer format to a packed 128-byte bank voice. */
export function packDx7Voice(unpacked: Uint8Array): Dx7Voice {
  if (unpacked.length !== FM1_VOICE_PARAMETER_COUNT) {
    throw new Error(
      `Expected ${FM1_VOICE_PARAMETER_COUNT} DX7 edit-buffer bytes; received ${unpacked.length}.`,
    )
  }
  if (unpacked.some((value) => value > 0x7f)) {
    throw new Error('DX7 edit-buffer data must contain only 7-bit values.')
  }

  const packed = new Uint8Array(dx7PackedVoiceSize)
  for (let operator = 0; operator < FM1_OPERATOR_COUNT; operator += 1) {
    const source = operator * FM1_OPERATOR_PARAMETER_COUNT
    const target = operator * 17
    packed.set(unpacked.slice(source, source + 11), target)
    packed[target + 11] = (unpacked[source + 11] & 0x03) | ((unpacked[source + 12] & 0x03) << 2)
    packed[target + 12] = (unpacked[source + 13] & 0x07) | ((unpacked[source + 20] & 0x0f) << 3)
    packed[target + 13] = (unpacked[source + 14] & 0x03) | ((unpacked[source + 15] & 0x07) << 2)
    packed[target + 14] = unpacked[source + 16]
    packed[target + 15] = (unpacked[source + 17] & 0x01) | ((unpacked[source + 18] & 0x1f) << 1)
    packed[target + 16] = unpacked[source + 19]
  }

  packed.set(unpacked.slice(FM1_GLOBAL_PARAMETER_START, feedbackIndex), 102)
  packed[111] = (unpacked[feedbackIndex] & 0x07) | ((unpacked[oscillatorSyncIndex] & 0x01) << 3)
  packed.set(unpacked.slice(lfoSpeedIndex, lfoKeySyncIndex), 112)
  packed[116] =
    (unpacked[lfoKeySyncIndex] & 0x01) |
    ((unpacked[lfoWaveIndex] & 0x07) << 1) |
    ((unpacked[pitchModSensitivityIndex] & 0x07) << 4)
  packed.set(unpacked.slice(transposeIndex, FM1_VOICE_PARAMETER_COUNT), 117)

  return { data: packed, name: decodeVoiceName(packed) }
}

/** Throws unless `channel` is a MIDI channel, an integer from 1 to 16. */
export function assertMidiChannel(channel: number) {
  if (!Number.isInteger(channel) || channel < 1 || channel > 16) {
    throw new RangeError('MIDI channel must be an integer from 1 to 16.')
  }
}

/** Yamaha DX7 single-voice bulk dump, excluding the F0/43 manufacturer prefix and F7 terminator. */
export function makeDx7SingleVoicePayload(voice: Dx7Voice, channel = 1) {
  assertMidiChannel(channel)
  assertPackedVoice(voice)
  const data = unpackDx7Voice(voice)
  const checksum = dx7Checksum(data)

  return Uint8Array.from([channel - 1, 0x00, 0x01, 0x1b, ...data, checksum])
}

/** Yamaha DX7 32-voice bulk dump, excluding the F0/43 manufacturer prefix and F7 terminator. */
export function makeDx7BankPayload(voices: Dx7Voice[], channel = 1) {
  assertMidiChannel(channel)
  if (voices.length !== dx7BankVoiceCount) {
    throw new Error(`A DX7 bank must contain exactly ${dx7BankVoiceCount} voices.`)
  }
  voices.forEach(assertPackedVoice)

  const data = Uint8Array.from(voices.flatMap((voice) => Array.from(voice.data)))
  const checksum = dx7Checksum(data)

  return Uint8Array.from([channel - 1, 0x09, 0x20, 0x00, ...data, checksum])
}

/** Yamaha's MIDI manufacturer ID, which follows F0 in every Yamaha SysEx message. */
export const yamahaManufacturerId = 0x43

/** A whole SysEx message from a Yamaha payload: F0, the manufacturer ID, the payload, and F7. */
export function makeYamahaSysexMessage(payload: Uint8Array) {
  return Uint8Array.from([0xf0, yamahaManufacturerId, ...payload, 0xf7])
}

/** Complete Yamaha DX7 32-voice SysEx file, ready to save as a .syx file. */
export function makeDx7BankFile(voices: Dx7Voice[], channel = 1) {
  return makeYamahaSysexMessage(makeDx7BankPayload(voices, channel))
}

/** The name in a packed voice's last ten bytes, as the FM1 shows it. */
export function decodeVoiceName(data: Uint8Array) {
  return (
    String.fromCharCode(...data.slice(118, 128))
      .replace(/[^\x20-\x7e]/g, ' ')
      .trim() || 'UNTITLED'
  )
}
